import { Entity } from '@backstage/catalog-model';

type EditEntityResult = {
  editForm?: object | undefined;
  cloneForm?: object | undefined;
  title: string;
  pathname: (template: 'edit' | 'create') => string;
};

export const EditEntity = (entity: Entity): EditEntityResult => {
  const title = entity.metadata.name;
  const type = entity.kind.toLowerCase() as keyof typeof entityEditFunctions;

  const isRegionalSystem =
    entity.kind === 'System' &&
    entity.metadata.labels?.['system-type'] === 'regional';

  const resourceType = entity.spec?.type as string | undefined;
  const dedicatedResourceTemplates: Record<string, string> = {
    'cosmosdb-account': 'edit-cosmosdb-account-template',
    'storage-account': 'edit-storage-account-template',
    'mssqlserver': 'edit-mssqlserver-template',
    'postgresql': 'edit-postgresql-template',
    'redis-cache': 'edit-redis-template',
    'eventhub-namespace': 'edit-eventhub-namespace-template',
  };
  const dedicatedTemplate =
    entity.kind === 'Resource' && resourceType
      ? dedicatedResourceTemplates[resourceType]
      : undefined;

  const templateName = isRegionalSystem
    ? 'edit-regional-system-template'
    : dedicatedTemplate ?? `edit-${type}-template`;

  const pathname = (_template: 'edit' | 'create') =>
    `/create/templates/default/${templateName}`;

  let editForm, cloneForm: object | undefined;
  let annotations = [];

  for (const key in entity.metadata.annotations) {
    if (entity.metadata.annotations.hasOwnProperty(key)) {
      if (key.includes('nestle.com')) {
        if (
          key != 'nestle.com/entity-visibility' &&
          key != 'nestle.com/system-ownership' &&
          key != 'nestle.com/domain'
        ) {
          annotations.push({
            key: key,
            value: entity.metadata.annotations[key],
          });
        }
      }
    }
  }

  const entityMetadataCommon = {
    name: entity.metadata.name,
    description: entity.metadata.description || '',
    namespace: entity.metadata.namespace,
    annotations: annotations,
    tags: entity.metadata.tags || [],
    visibility:
      entity.metadata.annotations?.['nestle.com/entity-visibility'] ||
      'internal',
  };

  const entityDomainAndSystemOwnership = {
    owner: entity.spec?.owner || '',
    domain: entity.spec?.domain || '',
  };

  const subSystemEntityOwnership = {
    owner: entity.spec?.owner || '',
    system: entity.spec?.system || '',
    domain: entity.metadata.annotations?.['nestle.com/domain'],
    type: entity.spec?.type || '',
  };

  const entityEditFunctions = {
    domain: () => {
      return {
        ...entityMetadataCommon,
        ...entityDomainAndSystemOwnership,
      };
    },
    system: () => {
      const parseAnnotationArray = (str?: string): string[] => {
        if (!str) return [];
        try {
          const parsed = JSON.parse(str);
          if (Array.isArray(parsed)) return parsed.map(String);
        } catch {}
        return str.split(',').map(s => s.trim()).filter(Boolean);
      };
      return {
        ...entityMetadataCommon,
        ...entityDomainAndSystemOwnership,
        ownership:
          entity.metadata.annotations?.['nestle.com/system-ownership'] ||
          'fully-managed',
        entity: `system:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`,
        githubTeam:
          entity.metadata.annotations?.['br.nestle.com/githubTeam'] || '',
        features: parseAnnotationArray(
          entity.metadata.annotations?.['br.nestle.com/features'],
        ),
        techleads: parseAnnotationArray(
          entity.metadata.annotations?.['br.nestle.com/techleads'],
        ),
        members: parseAnnotationArray(
          entity.metadata.annotations?.['br.nestle.com/members'],
        ),
      };
    },
    resource: () => {
      const base: Record<string, any> = {
        ...entityMetadataCommon,
        ...subSystemEntityOwnership,
        dependsOn: entity.spec?.dependsOn || [],
        dependencyOf: entity.spec?.dependencyOf || [],
        entity: `resource:${entity.metadata.namespace || 'default'}/${
          entity.metadata.name
        }`,
      };

      const annotation = (...keys: string[]): string | undefined => {
        for (const key of keys) {
          const value = entity.metadata.annotations?.[key];
          if (value) return value;
        }
        return undefined;
      };

      const parseJsonArray = (raw?: string): any[] | undefined => {
        if (!raw) return undefined;
        try {
          let parsed = JSON.parse(raw);
          // The njk template double-encodes the value (`| dump` on an
          // already-stringified array), so unwrap one extra level if needed.
          if (typeof parsed === 'string') parsed = JSON.parse(parsed);
          return Array.isArray(parsed) ? parsed : undefined;
        } catch {
          return undefined;
        }
      };

      if (resourceType === 'cosmosdb-account') {
        const parsed = parseJsonArray(
          annotation('br.nestle.com/children', 'nestle.com/children'),
        );
        if (parsed) base.cosmosDB = parsed;
      }

      if (resourceType === 'storage-account') {
        const parsed = parseJsonArray(
          annotation('br.nestle.com/children', 'nestle.com/children'),
        );
        if (parsed) {
          base.storagecontainers = parsed.filter((c: any) => c.type === 'storagecontainer');
          base.storagequeues = parsed.filter((c: any) => c.type === 'storagequeue');
          base.storagefileshares = parsed.filter((c: any) => c.type === 'storagefileshare');
        }
      }

      if (resourceType === 'postgresql') {
        base.postgresql = {
          databaseName:
            annotation('br.nestle.com/database-name', 'nestle.com/database-name') || '',
          plugins:
            parseJsonArray(
              annotation('br.nestle.com/plugins', 'nestle.com/plugins'),
            ) || [],
        };
      }

      if (resourceType === 'redis-cache') {
        base.redisCache = {
          skuName:
            annotation('br.nestle.com/sku-name', 'nestle.com/sku-name') || '',
          family: annotation('br.nestle.com/family', 'nestle.com/family') || '',
          capacity:
            annotation('br.nestle.com/capacity', 'nestle.com/capacity') || '',
        };
      }

      if (resourceType === 'eventhub-namespace') {
        const parsed = parseJsonArray(
          annotation('br.nestle.com/children', 'nestle.com/event-hubs'),
        );
        if (parsed) base.eventHubs = parsed;
      }
      return base;
    },
    component: () => {
      return {
        ...entityMetadataCommon,
        ...subSystemEntityOwnership,
        lifecycle: entity.spec?.lifecycle || '',
        subcomponentOf: entity.spec?.subcomponentOf || '',
        providesApis: entity.spec?.providesApis || [],
        consumesApis: entity.spec?.consumesApis || [],
        dependsOn: entity.spec?.dependsOn || [],
        dependencyOf: entity.spec?.dependencyOf || [],
      };
    },
    api: () => {
      return {
        ...entityMetadataCommon,
        ...subSystemEntityOwnership,
        lifecycle: entity.spec?.lifecycle || '',
        definition: entity.spec?.definition || '',
      };
    },
  };

  const entityCloneFunctions = {
    domain: () => entityEditFunctions['domain'](),
    resource: () => entityEditFunctions['resource'](),
    component: () => entityEditFunctions['component'](),
    api: () => entityEditFunctions['api'](),
  };

  if (Object.keys(entityEditFunctions).includes(type)) {
    editForm = entityEditFunctions[type]();
  }

  if (Object.keys(entityCloneFunctions).includes(type)) {
    cloneForm = entityEditFunctions[type]();
  }

  return {
    editForm,
    cloneForm,
    title,
    pathname,
  };
};
